import React from "react";
import { formatToDateFull } from "../master/FormatToDateTime";
import {
  buildMarksheet,
  getRemark,
  nextClass,
  getSummaryItems,
  FILL_IN_ITEMS,
} from "./marksheetUtils";
import {
  useSetting,
  getLogo,
  schoolAddress,
  schoolContact,
  getStudentFields,
  MarksheetStyle,
  MarksTable,
  CoScholastic,
  GradeLegend,
} from "./marksheetShared";

// Marksheet 2: Modern minimal. No borders, soft grey panels, zebra rows.

const CSS = `
& { padding: 11mm 12mm; }
& .ms2-head { display: flex; align-items: center; gap: 14px; }
& .ms2-logo { width: 62px; height: 62px; object-fit: contain; }
& .ms2-school { flex: 1; }
& .ms2-name { font-size: 21px; font-weight: 800; line-height: 1.15; }
& .ms2-meta { font-size: 10px; opacity: .75; }
& .ms2-doc { text-align: right; }
& .ms2-doc-title { font-size: 18px; font-weight: 800; letter-spacing: .03em; }

& .ms2-student { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px 14px; padding: 10px 14px; margin: 12px 0; border-radius: 10px; background: rgba(0,0,0,.055); }
& .ms2-field span { display: block; font-size: 9px; opacity: .65; }
& .ms2-field b { font-size: 11.5px; font-weight: 700; overflow-wrap: anywhere; }

& .ms-marks, & .ms-cos { border-collapse: separate; border-spacing: 0; border-radius: 10px; overflow: hidden; }
& .ms-marks th, & .ms-cos th { padding: 6px; text-align: center; font-weight: 800; background: rgba(0,0,0,.11); }
& .ms-marks td, & .ms-cos td { padding: 5px 6px; text-align: center; }
& .ms-marks tbody tr:nth-child(even) td, & .ms-cos tbody tr:nth-child(even) td { background: rgba(0,0,0,.05); }
& .ms-marks tfoot td { font-weight: 800; background: rgba(0,0,0,.11); }
& .ms-cos th:first-child, & .ms-cos td:first-child { text-align: left; }
& .ms-cos td { padding: 6px 8px; }

& .ms2-tiles { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 12px 0; }
& .ms2-tile { min-height: 46px; padding: 7px 10px; border-radius: 10px; background: rgba(0,0,0,.055); }
& .ms2-tile span { display: block; font-size: 9px; opacity: .65; }
& .ms2-tile b { font-size: 15px; font-weight: 800; }

& .ms2-lower { display: grid; grid-template-columns: 1.15fr 1fr; gap: 16px; align-items: start; }
& .ms2-note { margin-bottom: 8px; }
& .ms2-note span { display: block; font-size: 9px; opacity: .65; }
& .ms2-note p { min-height: 20px; font-weight: 600; }

& .ms2-sign { display: flex; gap: 24px; margin-top: 28px; }
& .ms2-sign div { flex: 1; text-align: center; padding-top: 4px; border-top: 1px solid #000 !important; }
& .ms-legend { margin-top: 14px; opacity: .85; }
`;

export default function StudentMarksheet2({ data, batch, examDate }) {
  const setting = useSetting();
  const sheet = buildMarksheet(data, batch);
  const logo = getLogo(setting);
  const fields = getStudentFields(data, batch);
  const tiles = [
    ...getSummaryItems(sheet.overall),
    ...FILL_IN_ITEMS.map((label) => ({ label, value: "" })),
  ];
  const remark = getRemark(sheet.overall.percentage, data?.student_id);

  return (
    <>
      <MarksheetStyle setting={setting} scope="ms2" css={CSS} />

      <div className="a4-page-marksheet ms2">
        <div className="ms2-head">
          {logo && <img className="ms2-logo" src={logo} alt="" />}
          <div className="ms2-school">
            <div className="ms2-name">{setting?.name}</div>
            <div className="ms2-meta">{schoolAddress(setting)}</div>
            <div className="ms2-meta">{schoolContact(setting)}</div>
          </div>
          <div className="ms2-doc">
            <div className="ms2-doc-title">Report Card</div>
            <div className="ms2-meta">Session {batch?.sessions?.session}</div>
          </div>
        </div>

        <div className="ms2-student">
          {fields.map((f) => (
            <div className="ms2-field" key={f.label}>
              <span>{f.label}</span>
              <b>{f.value || "-"}</b>
            </div>
          ))}
        </div>

        <MarksTable {...sheet} />

        <div className="ms2-tiles">
          {tiles.map((t) => (
            <div className="ms2-tile" key={t.label}>
              <span>{t.label}</span>
              <b>{t.value}</b>
            </div>
          ))}
        </div>

        <div className="ms2-lower">
          <CoScholastic />
          <div>
            <div className="ms2-note">
              <span>Class teacher's remark</span>
              <p>{remark}</p>
            </div>
            <div className="ms2-note">
              <span>Promoted to class</span>
              <p>{nextClass(batch?.course?.title)}</p>
            </div>
            <div className="ms2-note">
              <span>Date</span>
              <p>{formatToDateFull(examDate)}</p>
            </div>
          </div>
        </div>

        <div className="ms2-sign">
          <div>Class Teacher</div>
          <div>Parent</div>
          <div>Principal</div>
        </div>

        <GradeLegend />
      </div>
    </>
  );
}
